import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FolderIcon,
  FolderOpenIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentIcon,
  PhotoIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import pimService from '../../services/pimService';

interface MediaLibrary {
  id: number;
  name: string;
  slug: string;
  description?: string;
  type: 'general' | 'product' | 'category' | 'instruction' | 'certificate' | 'marketing';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  media_count?: number;
  total_size?: number;
}

interface MediaLibraryFormData {
  name: string;
  description: string;
  type: 'general' | 'product' | 'category' | 'instruction' | 'certificate' | 'marketing';
  is_active: boolean;
}

const getLibraryTypes = (t: any) => [
  {
    value: 'general',
    label: t('pim.mediaLibraries.types.general'),
    icon: FolderIcon,
    color: 'gray',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-600',
    description: t('pim.mediaLibraries.typeDescriptions.general')
  },
  {
    value: 'product',
    label: t('pim.mediaLibraries.types.product'),
    icon: PhotoIcon,
    color: 'blue',
    bgClass: 'bg-blue-100',
    textClass: 'text-blue-600',
    description: t('pim.mediaLibraries.typeDescriptions.product')
  },
  {
    value: 'category',
    label: t('pim.mediaLibraries.types.category'),
    icon: TagIcon,
    color: 'green',
    bgClass: 'bg-green-100',
    textClass: 'text-green-600',
    description: t('pim.mediaLibraries.typeDescriptions.category')
  },
  {
    value: 'instruction',
    label: t('pim.mediaLibraries.types.instruction'),
    icon: DocumentIcon,
    color: 'purple',
    bgClass: 'bg-purple-100',
    textClass: 'text-purple-600',
    description: t('pim.mediaLibraries.typeDescriptions.instruction')
  },
  {
    value: 'certificate',
    label: t('pim.mediaLibraries.types.certificate'),
    icon: CheckCircleIcon,
    color: 'yellow',
    bgClass: 'bg-yellow-100',
    textClass: 'text-yellow-600',
    description: t('pim.mediaLibraries.typeDescriptions.certificate')
  },
  {
    value: 'marketing',
    label: t('pim.mediaLibraries.types.marketing'),
    icon: Squares2X2Icon,
    color: 'pink',
    bgClass: 'bg-pink-100',
    textClass: 'text-pink-600',
    description: t('pim.mediaLibraries.typeDescriptions.marketing')
  }
];

export const MediaLibraries: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canManagePim, canWriteProducts, canReadProducts } = usePermissions();
  const { showNotification } = useNotification();
  
  const [libraries, setLibraries] = useState<MediaLibrary[]>([]);
  const [selectedLibrary, setSelectedLibrary] = useState<MediaLibrary | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<string>('all');
  
  const [formData, setFormData] = useState<MediaLibraryFormData>({
    name: '',
    description: '',
    type: 'general',
    is_active: true
  });

  useEffect(() => {
    if (canReadProducts) {
      loadLibraries();
    }
  }, [canReadProducts]);

  const loadLibraries = async () => {
    try {
      setLoading(true);
      const response = await pimService.getMediaLibraries();
      setLibraries(response.data?.libraries || []);
    } catch (error) {
      showNotification(t('pim.mediaLibraries.messages.loadFailed'), 'error');
      setLibraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLibrary = () => {
    setFormData({
      name: '',
      description: '',
      type: 'general',
      is_active: true
    });
    setSelectedLibrary(null);
    setShowCreateModal(true);
  };

  const handleEditLibrary = (library: MediaLibrary) => {
    setFormData({
      name: library.name,
      description: library.description || '',
      type: library.type,
      is_active: library.is_active
    });
    setSelectedLibrary(library);
    setShowEditModal(true);
  };

  const handleSaveLibrary = async () => {
    try {
      setLoading(true);

      // Generate slug from name if creating new library
      const slug = selectedLibrary
        ? selectedLibrary.slug
        : formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

      const dataToSend = {
        ...formData,
        slug
      };

      if (selectedLibrary) {
        await pimService.updateMediaLibrary(selectedLibrary.id, dataToSend);
        showNotification(t('pim.mediaLibraries.messages.updated'), 'success');
      } else {
        await pimService.createMediaLibrary(dataToSend);
        showNotification(t('pim.mediaLibraries.messages.created'), 'success');
      }
      setShowCreateModal(false);
      setShowEditModal(false);
      loadLibraries();
    } catch (error) {
      showNotification(`${t('pim.mediaLibraries.messages.saveFailed')}: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLibrary = async (library: MediaLibrary, force: boolean = false) => {
    if (!force && !confirm(t('pim.mediaLibraries.confirmDelete', { name: library.name }))) {
      return;
    }

    try {
      setLoading(true);
      await pimService.deleteMediaLibrary(library.id, force);
      showNotification(t('pim.mediaLibraries.messages.deleted'), 'success');
      loadLibraries();
    } catch (error: any) {
      // Check if the error is about library containing media files
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';

      if (error?.response?.status === 400 && errorMessage.includes('media file')) {
        // Ask user if they want to force delete
        const forceDelete = confirm(
          `${errorMessage}\n\nCzy na pewno chcesz usunąć bibliotekę wraz ze wszystkimi plikami?`
        );
        if (forceDelete) {
          // Retry with force=true
          handleDeleteLibrary(library, true);
          return;
        }
      } else {
        showNotification(`${t('pim.mediaLibraries.messages.deleteFailed')}: ${errorMessage}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLibrary = (library: MediaLibrary) => {
    // Navigate to media management for this specific library
    navigate(`/pim/media?library=${library.id}`);
  };

  const LIBRARY_TYPES = getLibraryTypes(t);

  const getTypeConfig = (type: string) => {
    return LIBRARY_TYPES.find(t => t.value === type) || LIBRARY_TYPES[0];
  };

  const getFilteredLibraries = () => {
    let filtered = libraries;

    if (searchTerm) {
      filtered = filtered.filter(library => 
        library.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        library.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(library => library.type === filterType);
    }

    return filtered;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!canReadProducts) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.mediaLibraries.accessDenied')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('pim.mediaLibraries.accessDeniedDescription')}
          </p>
        </div>
      </div>
    );
  }

  const filteredLibraries = getFilteredLibraries();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.mediaLibraries.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('pim.mediaLibraries.subtitle')}
          </p>
        </div>
        {canWriteProducts && (
          <button
            onClick={handleCreateLibrary}
            className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('pim.mediaLibraries.create')}
          </button>
        )}
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('pim.mediaLibraries.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">{t('pim.mediaLibraries.filters.allTypes')}</option>
              {LIBRARY_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'grid'
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-l-0 border ${
                viewMode === 'list'
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-1">
            <FolderIcon className="h-5 w-5 text-gray-400" />
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('pim.mediaLibraries.stats.totalLibraries')}</div>
          </div>
          <div className="text-2xl font-bold text-gray-900">{libraries.length}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="text-xs font-medium text-green-600 uppercase tracking-wide">{t('pim.mediaLibraries.stats.activeLibraries')}</div>
          </div>
          <div className="text-2xl font-bold text-green-600">
            {libraries.filter(l => l.is_active).length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-1">
            <PhotoIcon className="h-5 w-5 text-blue-400" />
            <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">{t('pim.mediaLibraries.stats.totalFiles')}</div>
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {libraries.reduce((sum, l) => sum + (l.media_count || 0), 0)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-1">
            <DocumentIcon className="h-5 w-5 text-purple-400" />
            <div className="text-xs font-medium text-purple-600 uppercase tracking-wide">{t('pim.mediaLibraries.stats.totalSize')}</div>
          </div>
          <div className="text-2xl font-bold text-purple-600">
            {formatFileSize(libraries.reduce((sum, l) => sum + (l.total_size || 0), 0))}
          </div>
        </div>
      </div>

      {/* Libraries Display */}
      {loading && filteredLibraries.length === 0 ? (
        <div className="flex justify-center py-12">
          <div className="text-gray-500">{t('pim.mediaLibraries.loading')}</div>
        </div>
      ) : filteredLibraries.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {searchTerm || filterType !== 'all' ? t('pim.mediaLibraries.noResults') : t('pim.mediaLibraries.noLibraries')}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {searchTerm || filterType !== 'all'
              ? t('pim.mediaLibraries.noResultsDescription')
              : t('pim.mediaLibraries.noLibrariesDescription')
            }
          </p>
          {canWriteProducts && !searchTerm && filterType === 'all' && (
            <button
              onClick={handleCreateLibrary}
              className="mt-4 flex items-center mx-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('pim.mediaLibraries.createFirstLibrary')}
            </button>
          )}
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5' : 'space-y-4'}>
          {filteredLibraries.map(library => {
            const typeConfig = getTypeConfig(library.type);
            const IconComponent = typeConfig.icon;
            
            if (viewMode === 'grid') {
              return (
                <div
                  key={library.id}
                  className={`bg-white border-2 rounded-xl p-5 hover:shadow-lg hover:border-blue-200 transition-all duration-200 ${
                    !library.is_active ? 'opacity-60' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2.5 rounded-xl ${typeConfig.bgClass} ring-1 ring-gray-200`}>
                        <IconComponent className={`h-6 w-6 ${typeConfig.textClass}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{library.name}</h3>
                        <p className="text-xs font-medium text-gray-500 mt-0.5">{typeConfig.label}</p>
                      </div>
                    </div>
                    {!library.is_active && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                        {t('pim.mediaLibraries.status.inactive')}
                      </span>
                    )}
                  </div>

                  {library.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">{library.description}</p>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-1">
                      <PhotoIcon className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{library.media_count || 0}</span>
                      <span>{t('pim.mediaLibraries.files')}</span>
                    </div>
                    {library.total_size !== undefined && (
                      <div className="flex items-center gap-1 justify-end">
                        <DocumentIcon className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{formatFileSize(library.total_size)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenLibrary(library)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <FolderOpenIcon className="h-4 w-4" />
                      {t('pim.mediaLibraries.open')}
                    </button>
                    {canWriteProducts && (
                      <button
                        onClick={() => handleEditLibrary(library)}
                        className="px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                        title={t('common.actions.edit')}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                    {canManagePim && (
                      <button
                        onClick={() => handleDeleteLibrary(library)}
                        className="px-3 py-2 text-sm font-medium border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors"
                        title={t('common.actions.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  key={library.id}
                  className={`bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow ${
                    !library.is_active ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`p-2 rounded-lg ${typeConfig.bgClass}`}>
                        <IconComponent className={`h-5 w-5 ${typeConfig.textClass}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-sm font-medium text-gray-900 truncate">{library.name}</h3>
                          {!library.is_active && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {t('pim.mediaLibraries.status.inactive')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                          <span>{typeConfig.label}</span>
                          <span>{library.media_count || 0} {t('pim.mediaLibraries.files')}</span>
                          {library.total_size && <span>{formatFileSize(library.total_size)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleOpenLibrary(library)}
                        className="flex items-center px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
                      >
                        <FolderOpenIcon className="h-4 w-4 mr-1" />
                        {t('pim.mediaLibraries.open')}
                      </button>
                      {canWriteProducts && (
                        <button
                          onClick={() => handleEditLibrary(library)}
                          className="p-1.5 text-gray-400 hover:text-gray-600"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      {canManagePim && (
                        <button
                          onClick={() => handleDeleteLibrary(library)}
                          className="p-1.5 text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedLibrary ? t('pim.mediaLibraries.edit') : t('pim.mediaLibraries.modal.createTitle')}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('pim.mediaLibraries.fields.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('pim.mediaLibraries.fields.type')} *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {LIBRARY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('pim.mediaLibraries.fields.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  {t('pim.mediaLibraries.fields.activeLibrary')}
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                {t('common.actions.cancel')}
              </button>
              <button
                onClick={handleSaveLibrary}
                disabled={loading || !formData.name}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('pim.mediaLibraries.modal.saving') : t('pim.mediaLibraries.modal.saveButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibraries;